import axios from 'axios';
import { API_BASE_URL, STORAGE_KEYS } from '@/config/api.config';
import store from '@/shared/state/store';
import authStorage from './auth-storage.service';
import notificationService from '@/shared/services/notification.service';

// Queue for requests waiting for token refresh
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Request interceptor - Add JWT to all requests
axios.interceptors.request.use(
  (config) => {
    const token = store.getters['auth/accessToken'];

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // JSON par défaut, mais seulement pour un corps qui en est. Un envoi de
    // fichier passe en multipart, et c'est axios qui doit poser l'en-tête: elle
    // porte la frontière qui sépare les parties. La forcer en JSON laisse le
    // serveur ne voir aucun fichier.
    const carriesOwnType =
      typeof FormData !== 'undefined' && config.data instanceof FormData;

    if (!config.headers['Content-Type'] && !carriesOwnType) {
      config.headers['Content-Type'] = 'application/json';
    }

    return config;
  },
  (error) => {
    // When no specific handling was added above for 401, display a toast for other errors
    try {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        'Erreur lors de la requête.';
      notificationService.toastError(msg);
    } catch (e) {
      // ignore
    }
    return Promise.reject(error);
  },
);

// Response interceptor - Handle 401 errors and token refresh
axios.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (originalRequest.__skipRefresh) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Another request is already refreshing, queue this one
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axios(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Use Vuex action to refresh token
        await store.dispatch('auth/refreshToken');

        const newToken = store.getters['auth/accessToken'];

        // Process queued requests
        processQueue(null, newToken);

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axios(originalRequest);
      } catch (refreshError) {
        // Le rafraîchissement a échoué : la session est morte, on la ferme.
        // La purge se fait en local, sans repasser par l'API. L'appel de
        // déconnexion partait par le même axios, revenait lui aussi en 401 et
        // se rangeait dans la file d'attente derrière un rafraîchissement
        // encore marqué en cours : sa promesse ne retombait jamais, les clés
        // de session restaient en place et la redirection n'arrivait pas.
        // L'utilisateur restait sur une page vide, session morte en main.
        processQueue(refreshError, null);
        isRefreshing = false;
        store.dispatch('auth/logoutLocal');
        redirectToLogin();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

function redirectToLogin() {
  if (
    window.location.pathname !== '/login' &&
    !window.location.pathname.startsWith('/login')
  ) {
    const currentPath = window.location.pathname + window.location.search;
    if (currentPath !== '/' && currentPath !== '/login') {
      authStorage.setItem(STORAGE_KEYS.REDIRECT_AFTER_LOGIN, currentPath);
    }

    window.location.href = '/login';
  }
}
export const refreshClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
});
export default axios;
